import dayjs from 'dayjs';
import { dset } from 'dset/merge';
import {
	DateFieldConfiguration,
	GroupFieldConfiguration,
	MediaFieldConfiguration,
	SelectFieldConfiguration,
	StringFieldConfiguration,
} from '../../../api/ConfigApi';
import { AuthData } from '../../../context/auth';
import { MediaResponse } from '../../../types/media';
import { FIELD_TYPES } from '../data/constants';
import {
	DefaultValuesType,
	FieldTypes,
	FormBuilder,
	FormShape,
} from '../types';
import { dateConversion, filterObjectByKeys } from '../utils';

const getDefaultPolicyValues = (
	form: FormShape<FormBuilder>,
	authData: AuthData,
) => {
	if (!form) {
		return {};
	}
	const defaultValues = {} as DefaultValuesType;
	let defaultValue = '' as FieldTypes;

	for (const subForm in form) {
		if (form[subForm].fields) {
			const fields = form[subForm].fields;
			const fieldValues = form[subForm].values;
			const fieldGroupsByLocator = form[subForm].fieldGroupsByLocator;
			const addFieldGroups = form[subForm].addFieldGroups;
			const fieldGroups = form[subForm].fieldGroups;
			const mediaByLocator = form[subForm].mediaByLocator;
			fields.forEach((field) => {
				switch (field.type) {
					case FIELD_TYPES.string:
					case FIELD_TYPES.number:
					case FIELD_TYPES.currency:
					case FIELD_TYPES.email:
					case FIELD_TYPES.lookup:
					case FIELD_TYPES.policyLookup:
					case FIELD_TYPES.policyholderLookup:
						const stringField = field as StringFieldConfiguration;
						defaultValue =
							fieldValues[stringField.name] &&
							fieldValues[field.name].length > 0
								? fieldValues[field.name][0]
								: '';
						break;
					case FIELD_TYPES.select:
						const selectField = field as SelectFieldConfiguration;
						if (selectField.repeatable) {
							defaultValue =
								fieldValues[field.name] && fieldValues[field.name].length > 0
									? fieldValues[field.name]
									: [];
						}
						if (!selectField.repeatable) {
							defaultValue =
								fieldValues[field.name] && fieldValues[field.name].length > 0
									? fieldValues[field.name][0]
									: '';
						}
						break;
					case FIELD_TYPES.date:
						const dateField = field as DateFieldConfiguration;
						const initialValue = fieldValues[dateField.name]
							? fieldValues[dateField.name][0]
							: '';
						if (initialValue === '') {
							defaultValue = null;
						} else if (dateField.ontologyType) {
							const parsedDate =
								dateConversion.storedPolicyholderDateToDateString(initialValue);
							defaultValue = parsedDate;
						} else {
							const zonedDateObject =
								dateConversion.backendFormattedStringToLocalDayJs(
									initialValue,
									dateField.precision,
								);
							if (dayjs.isDayjs(zonedDateObject) && zonedDateObject.isValid()) {
								defaultValue = dateConversion.dayJsObjectToDateFormString(
									zonedDateObject,
									dateField.precision,
								);
							} else {
								defaultValue = '';
							}
						}
						break;
					case FIELD_TYPES.group:
						const groupField = field as GroupFieldConfiguration;
						const groupFieldValues = [] as DefaultValuesType[];
						fieldValues[groupField.name] &&
							fieldValues[groupField.name].forEach((fieldLocator) => {
								const groupValues = fieldGroupsByLocator[fieldLocator];
								const groupForm = {
									[groupField.name as keyof FormBuilder]: {
										fields: groupField.fields,
										values: groupValues ? groupValues : {},
										fieldGroupsByLocator: {},
										mediaByLocator,
									} as FormBuilder,
								} as FormShape<FormBuilder>;
								groupFieldValues.push(
									getDefaultPolicyValues(groupForm, authData),
								);
							});
						if (addFieldGroups && addFieldGroups.length > 0) {
							addFieldGroups.forEach((fieldGroup, index) => {
								if (fieldGroup.fieldName === groupField.name) {
									const groupForm = {
										[fieldGroup.fieldName as keyof FormBuilder]: {
											fields: groupField.fields,
											values: fieldGroup.fieldValues,
											fieldGroupsByLocator: {},
											mediaByLocator,
										} as FormBuilder,
									} as FormShape<FormBuilder>;
									groupFieldValues.push(
										getDefaultPolicyValues(groupForm, authData),
									);
								}
							});
						}
						if (fieldGroups && fieldGroups.length > 0) {
							fieldGroups.forEach((fieldGroup, index) => {
								if (fieldGroup.fieldName === groupField.name) {
									const groupForm = {
										[fieldGroup.fieldName as keyof FormBuilder]: {
											fields: groupField.fields,
											values: fieldGroup.fieldValues,
											fieldGroupsByLocator: {},
											mediaByLocator,
										} as FormBuilder,
									} as FormShape<FormBuilder>;
									groupFieldValues.push(
										getDefaultPolicyValues(groupForm, authData),
									);
								}
							});
						}
						if (groupFieldValues.length > 0) {
							groupFieldValues.forEach((groupFieldForm, index) => {
								dset(
									defaultValues,
									`${subForm}.${groupField.name}.${index}`,
									groupFieldForm[groupField.name],
								);
							});
						} else {
							dset(defaultValues, `${subForm}.${groupField.name}`, []);
						}
						break;
					case FIELD_TYPES.media:
						const mediaField = field as MediaFieldConfiguration;
						const values = [] as MediaResponse[];
						fieldValues[mediaField.name] &&
							fieldValues[mediaField.name].forEach((mediaLocator) => {
								values.push({
									locator: mediaLocator,
								});
							});
						defaultValue = values;
				}

				if (field.type !== FIELD_TYPES.group) {
					dset(defaultValues, subForm + '.' + field.name, defaultValue);
				}
			});
		}
		const filtered = filterObjectByKeys(
			form[subForm] as FormShape<FormBuilder>,
		);
		if (Object.keys(filtered).length && Object.keys(filtered).length > 0) {
			Object.keys(filtered).forEach((key) => {
				if (filtered[key] && typeof filtered[key] === 'object') {
					const subSubForm = { [key]: filtered[key] } as FormShape<FormBuilder>;
					const subSubFormVales = getDefaultPolicyValues(subSubForm, authData);
					dset(defaultValues, subForm + '.' + key, subSubFormVales[key]);
				}
			});
		}
	}

	return defaultValues;
};

export default getDefaultPolicyValues;
